import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAppContext } from '../../context/AppContext';
import CartItem from './CartItem';

const FloatingCartButton = () => {
    const { items, total, clearCart } = useCart();
    const { settings } = useAppContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [pulseAnimation, setPulseAnimation] = useState(false);
    const [bounceAnimation, setBounceAnimation] = useState(false);

    const itemCount = items.reduce((count, item) => count + item.quantity, 0);

    const isPaymentScreen = location.pathname === '/payment';

    const ivaAmount = total * 0.19;
    const subtotalWithoutTax = total - ivaAmount;

    useEffect(() => {
        if (itemCount > 0) {
            setPulseAnimation(true);
            setBounceAnimation(true);
            const timer1 = setTimeout(() => setPulseAnimation(false), 600);
            const timer2 = setTimeout(() => setBounceAnimation(false), 800);
            return () => {
                clearTimeout(timer1);
                clearTimeout(timer2);
            };
        }
    }, [itemCount]);

    const hiddenPaths = ['/login', '/thank-you', '/payment', '/payment-method', '/operate-card'];
    if (hiddenPaths.includes(location.pathname)) {
        return null;
    }

    const handleToggleCart = () => {
        if (isOpen) {
            setIsAnimating(true);
            setTimeout(() => {
                setIsOpen(false);
                setIsAnimating(false);
            }, 250);
        } else {
            setIsOpen(true);
        }
    };

    const handleProceedToPayment = () => {
        if (items.length === 0) {
            alert('Tu carrito está vacío. Agrega productos para proceder al pago.');
            return;
        }

        const missingOptions = items.filter(item => {
            if (item.attributes && item.attributes.length > 0) {
                return !item.selectedOptions || Object.keys(item.selectedOptions).length !== item.attributes.length;
            }
            return false;
        });

        if (missingOptions.length > 0) {
            alert(
                'Por favor, selecciona todas las opciones requeridas para los siguientes productos: ' +
                missingOptions.map(item => item.name).join(', ')
            );
            return;
        }

        setIsOpen(false);
        navigate('/payment');
    };

    return (
        <>
            <div className="floating-cart-container">
                <button
                    onClick={handleToggleCart}
                    className="floating-cart-button"
                >
                    <div className="cart-background" />

                    <div className="cart-icon-container">
                        <svg
                            className="cart-icon"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                        </svg>
                    </div>

                    {itemCount > 0 && (
                        <div className="item-counter">
                            <span className="counter-text">
                                {itemCount > 99 ? '99+' : itemCount}
                            </span>
                        </div>
                    )}

                    {total > 0 && (
                        <div className="price-indicator">
                            ${total.toLocaleString()}
                        </div>
                    )}
                </button>
            </div>

            {isOpen && (
                <div className="cart-modal-overlay">
                    <div
                        className="cart-backdrop"
                        onClick={handleToggleCart}
                    />

                    <div className="cart-modal-content">
                        <div className="cart-modal-header">
                            <div className="header-content">
                                <div className="cart-icon-large">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                                <div className="header-text">
                                    <h2 className="modal-title">Tu Pedido</h2>
                                    <p className="modal-subtitle">
                                        {itemCount} {itemCount === 1 ? 'producto' : 'productos'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleToggleCart}
                                className="close-button-cart"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="cart-content">
                            {items.length === 0 ? (
                                <div className="empty-cart">
                                    <div className="empty-cart-icon">
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="empty-title">Tu carrito está vacío</h3>
                                    <p className="empty-subtitle">Añade productos para empezar tu pedido</p>
                                </div>
                            ) : (
                                <div className="cart-items">
                                    {items.map((item, index) => (
                                        <div key={item.id} className="cart-item-wrapper">
                                            <CartItem
                                                item={item}
                                                isPaymentScreen={isPaymentScreen}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {items.length > 0 && (
                            <div className="cart-modal-footer">
                                <div className="price-summary">
                                    <div className="price-row">
                                        <span className="price-label">Subtotal (Neto):</span>
                                        <span className="price-value">
                                            ${subtotalWithoutTax.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                        </span>
                                    </div>
                                    <div className="price-row">
                                        <span className="price-label">IVA (19%):</span>
                                        <span className="price-value">
                                            ${ivaAmount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                        </span>
                                    </div>
                                    <div className="price-row total-row">
                                        <span className="total-label">Total:</span>
                                        <span className="total-value">${total.toLocaleString()}</span>
                                    </div>
                                </div>

                                <div className="action-buttons">
                                    <button
                                        onClick={handleProceedToPayment}
                                        disabled={items.length === 0}
                                        className="checkout-button"
                                    >
                                        <span className="button-text">Proceder al pago</span>
                                        <div className="button-icon">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </button>

                                    <button
                                        onClick={clearCart}
                                        className="clear-button"
                                    >
                                        Vaciar carrito
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style jsx>{`
                .floating-cart-container {
                    position: fixed;
                    bottom: 2rem;
                    right: 2rem;
                    z-index: 40;
                    transform: ${pulseAnimation ? 'scale(1.15)' : 'scale(1)'};
                    transition: transform 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55);
                }

                .floating-cart-button {
                    position: relative;
                    width: 80px;
                    height: 80px;
                    border: none;
                    background: none;
                    cursor: pointer;
                    padding: 0;
                    outline: none;
                    transform: ${bounceAnimation ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0deg)'};
                    transition: all 250ms cubic-bezier(0.68, -0.55, 0.265, 1.55);
                }

                .floating-cart-button:hover {
                    transform: scale(1.05) rotate(-2deg);
                }

                .floating-cart-button:active {
                    transform: scale(0.95);
                }

                .cart-background {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(135deg, ${settings.button_color}, ${settings.button_color}dd);
                    border-radius: 50%;
                    box-shadow:
                        0 8px 32px ${settings.button_color}60,
                        0 4px 16px ${settings.button_color}40,
                        inset 0 2px 4px rgba(255, 255, 255, 0.2);
                    transition: all 200ms ease;
                }

                .floating-cart-button:hover .cart-background {
                    box-shadow:
                        0 12px 40px ${settings.button_color}70,
                        0 6px 20px ${settings.button_color}50,
                        inset 0 2px 4px rgba(255, 255, 255, 0.3);
                }

                .cart-icon-container {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                }

                .cart-icon {
                    width: 36px;
                    height: 36px;
                    color: white;
                    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
                }

                .item-counter {
                    position: absolute;
                    top: -8px;
                    right: -8px;
                    min-width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #ff4444, #ff6666);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow:
                        0 4px 16px rgba(255, 68, 68, 0.4),
                        0 2px 8px rgba(0, 0, 0, 0.2);
                    transform: ${itemCount > 0 ? 'scale(1)' : 'scale(0)'};
                    transition: transform 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55);
                    border: 3px solid white;
                }

                .counter-text {
                    color: white;
                    font-size: 0.875rem;
                    font-weight: 900;
                    line-height: 1;
                    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
                }

                .price-indicator {
                    position: absolute;
                    bottom: -16px;
                    left: 50%;
                    transform: translateX(-50%) ${total > 0 ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(10px)'};
                    background: linear-gradient(135deg, white, #f8f9fa);
                    color: ${settings.button_color};
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    font-size: 0.875rem;
                    font-weight: 900;
                    white-space: nowrap;
                    box-shadow:
                        0 6px 20px rgba(0, 0, 0, 0.15),
                        0 2px 8px rgba(0, 0, 0, 0.1);
                    opacity: ${total > 0 ? 1 : 0};
                    transition: all 400ms cubic-bezier(0.68, -0.55, 0.265, 1.55);
                    border: 2px solid ${settings.button_color}30;
                }

                .cart-modal-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 50;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1rem;
                    animation: overlayEnter 250ms ease-out;
                }

                .cart-backdrop {
                    position: absolute;
                    inset: 0;
                    background-color: ${isAnimating ? 'rgba(0, 0, 0, 0)' : 'rgba(0, 0, 0, 0.6)'};
                    backdrop-filter: blur(4px);
                    transition: all 250ms ease-out;
                }

                .cart-modal-content {
                    position: relative;
                    width: 100%;
                    max-width: 600px;
                    max-height: 90vh;
                    background: ${settings.card_color};
                    border-radius: 24px;
                    overflow: hidden;
                    box-shadow:
                        0 25px 50px rgba(0, 0, 0, 0.25),
                        0 10px 30px rgba(0, 0, 0, 0.15);
                    transform: ${isAnimating ? 'scale(0.9) translateY(20px)' : 'scale(1) translateY(0)'};
                    opacity: ${isAnimating ? 0 : 1};
                    transition: all 250ms cubic-bezier(0.68, -0.55, 0.265, 1.55);
                    display: flex;
                    flex-direction: column;
                    animation: modalEnter 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55);
                }

                .cart-modal-header {
                    background: linear-gradient(135deg, ${settings.button_color}, ${settings.button_color}dd);
                    padding: 1.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                }

                .header-content {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .cart-icon-large {
                    width: 48px;
                    height: 48px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    backdrop-filter: blur(10px);
                }

                .cart-icon-large svg {
                    width: 24px;
                    height: 24px;
                    color: white;
                }

                .header-text {
                    color: white;
                }

                .modal-title {
                    font-size: 1.5rem;
                    font-weight: 800;
                    margin: 0 0 0.25rem 0;
                    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
                }

                .modal-subtitle {
                    font-size: 0.875rem;
                    margin: 0;
                    opacity: 0.9;
                }

                .close-button-cart {
                    width: 48px;
                    height: 48px;
                    background: rgba(255, 255, 255, 0.15);
                    border: none;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 200ms ease;
                    backdrop-filter: blur(10px);
                }

                .close-button-cart:hover {
                    background: rgba(255, 255, 255, 0.25);
                    transform: scale(1.1);
                }

                .close-button-cart svg {
                    width: 32px;
                    height: 32px;
                    color: white;
                }

                .cart-content {
                    flex: 1;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .empty-cart {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 4rem 2rem;
                    text-align: center;
                }

                .empty-cart-icon {
                    width: 80px;
                    height: 80px;
                    color: #cbd5e1;
                    margin-bottom: 1.5rem;
                    opacity: 0.5;
                }

                .empty-cart-icon svg {
                    width: 100%;
                    height: 100%;
                }

                .empty-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #374151;
                    margin: 0 0 0.5rem 0;
                }

                .empty-subtitle {
                    color: #6b7280;
                    margin: 0;
                }

                .cart-items {
                    padding: 1rem;
                    overflow-y: auto;
                    flex: 1;
                    scrollbar-width: thin;
                    scrollbar-color: #cbd5e1 transparent;
                }

                .cart-items::-webkit-scrollbar {
                    width: 6px;
                }

                .cart-items::-webkit-scrollbar-track {
                    background: transparent;
                }

                .cart-items::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 3px;
                }

                .cart-item-wrapper {
                    animation: slideInUp 300ms ease-out both;
                }

                .cart-item-wrapper:nth-child(n) {
                    animation-delay: calc(var(--index, 0) * 50ms);
                }

                .cart-modal-footer {
                    background: ${settings.box_color};
                    padding: 1.5rem;
                    border-top: 1px solid #e5e7eb;
                    animation: slideInUp 400ms ease-out 200ms both;
                }

                .price-summary {
                    margin-bottom: 1.5rem;
                    padding: 1rem;
                    background: ${settings.card_color};
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                }

                .price-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                }

                .price-row:last-child {
                    margin-bottom: 0;
                }

                .price-label {
                    color: #6b7280;
                    font-size: 0.875rem;
                }

                .price-value {
                    font-weight: 600;
                    color: #374151;
                }

                .total-row {
                    padding-top: 0.75rem;
                    border-top: 2px solid #e5e7eb;
                    margin-top: 0.75rem;
                }

                .total-label {
                    font-size: 1rem;
                    font-weight: 700;
                    color: #1f2937;
                }

                .total-value {
                    font-size: 1.25rem;
                    font-weight: 900;
                    color: ${settings.button_color};
                }

                .action-buttons {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .checkout-button {
                    width: 100%;
                    background: linear-gradient(135deg, ${settings.button_color}, ${settings.button_color}dd);
                    color: white;
                    border: none;
                    padding: 1rem 1.5rem;
                    border-radius: 16px;
                    font-size: 1.1rem;
                    font-weight: 800;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    transition: all 200ms ease;
                    box-shadow:
                        0 8px 25px ${settings.button_color}40,
                        0 3px 10px rgba(0, 0, 0, 0.1);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .checkout-button:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow:
                        0 12px 30px ${settings.button_color}50,
                        0 5px 15px rgba(0, 0, 0, 0.15);
                }

                .checkout-button:active:not(:disabled) {
                    transform: translateY(0);
                }

                .checkout-button:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                    box-shadow: none;
                }

                .button-text {
                    flex: 1;
                }

                .button-icon {
                    width: 20px;
                    height: 20px;
                    opacity: 0.8;
                }

                .button-icon svg {
                    width: 100%;
                    height: 100%;
                }

                .clear-button {
                    width: 100%;
                    background: #ef4444;
                    color: white;
                    border: none;
                    padding: 0.875rem 1.5rem;
                    border-radius: 12px;
                    font-size: 1rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 200ms ease;
                    box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
                }

                .clear-button:hover {
                    background: #dc2626;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
                }

                @keyframes overlayEnter {
                    0% {
                        opacity: 0;
                    }
                    100% {
                        opacity: 1;
                    }
                }

                @keyframes modalEnter {
                    0% {
                        opacity: 0;
                        transform: scale(0.8) translateY(40px);
                    }
                    50% {
                        opacity: 1;
                        transform: scale(1.02) translateY(-5px);
                    }
                    100% {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }

                @keyframes slideInUp {
                    0% {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </>
    );
};

export default FloatingCartButton;